import type { Feature } from "@turf/helpers";
import type { TFunction } from "react-i18next";
import type { TagProps } from "../../atoms/tag/Tag";
import type { IconName } from "../../core/icon/names";
import type { Question } from "./survey.types";

export interface PageTypeConfigItem {
  leadingIcon: IconName;
  swapGroup?: string;
  category?: string;
  color: TagProps["color"];
  defaults?: {
    options: number;
    minOptions?: number;
    maxOptions?: number;
    columns?: number;
    minColumns?: number;
    maxColumns?: number;
    allowEmpty?: readonly ["options" | "columns"];
  };
  sidebarTitle?: string;
  sidebarNumber?: string;
  legacy?: boolean;
  hideInSelection?: boolean;
  structuralPage?: boolean;
  sortable?: boolean;
  logic?: boolean;
}
export const pageTypeConfig = {
  text: {
    leadingIcon: "edit",
    category: "text",
    color: "teal-400",
    defaults: {
      columns: 1,
      maxColumns: 1,
      options: 1,
    },
  } satisfies PageTypeConfigItem,
  number: {
    leadingIcon: "number",
    category: "text",
    color: "teal-500",
    defaults: {
      columns: 1,
      maxColumns: 1,
      options: 1,
    },
  } satisfies PageTypeConfigItem,
  radio: {
    leadingIcon: "checkmark-xl",
    category: "selection",
    color: "orange-400",
    defaults: {
      minOptions: 1,
      options: 2,
    },
    logic: true,
  } satisfies PageTypeConfigItem,
  radioMatrix: {
    leadingIcon: "matrix",
    category: "selection",
    color: "orange-500",
    defaults: {
      options: 2,
      minOptions: 2,
      columns: 2,
      minColumns: 2,
      allowEmpty: ["columns"],
    },
  } satisfies PageTypeConfigItem,
  imageChoice: {
    leadingIcon: "image",
    category: "selection",
    color: "orange-600",
    defaults: {
      minOptions: 1,
      options: 2,
    },
    logic: true,
  } satisfies PageTypeConfigItem,
  ratingScale: {
    leadingIcon: "nps",
    category: "rating",
    color: "yellow-300",
    defaults: {
      columns: 3,
      minColumns: 2,
      maxColumns: 10,
      options: 1,
    },
  } satisfies PageTypeConfigItem,
  stars: {
    leadingIcon: "star-fill",
    category: "rating",
    color: "yellow-400",
    defaults: {
      options: 1,
      columns: 5,
      maxColumns: 5,
    },
  } satisfies PageTypeConfigItem,
  slider: {
    leadingIcon: "slider",
    category: "rating",
    color: "yellow-500",
    defaults: {
      options: 1,
      columns: 5,
      minColumns: 2,
      maxColumns: 100,
    },
  } satisfies PageTypeConfigItem,
  ranking: {
    leadingIcon: "ranking",
    category: "priority",
    color: "purple-400",
    defaults: {
      minOptions: 2,
      options: 2,
    },
  } satisfies PageTypeConfigItem,
  budget: {
    leadingIcon: "participatory-budget",
    category: "priority",
    color: "purple-500",
    defaults: {
      minOptions: 2,
      options: 2,
    },
  } satisfies PageTypeConfigItem,
  overview: {
    sidebarNumber: " ",
    leadingIcon: "statistics",
    color: "neutral-400",
    hideInSelection: true,
    structuralPage: true,
  } satisfies PageTypeConfigItem,
  intro: {
    sidebarNumber: "",
    leadingIcon: "first-screen-horizontal",
    category: "intro",
    color: "neutral-400",
    hideInSelection: true,
    structuralPage: true,
    sortable: false,
  } satisfies PageTypeConfigItem,
  outro: {
    sidebarNumber: "",
    leadingIcon: "last-screen-horizontal",
    category: "outro",
    color: "neutral-400",
    hideInSelection: true,
    structuralPage: true,
    sortable: false,
  } satisfies PageTypeConfigItem,
  point: {
    leadingIcon: "pin-alt",
    category: "map",
    color: "green-300",
    defaults: {
      minOptions: 2,
      options: 2,
    },
  } satisfies PageTypeConfigItem,
  path: {
    leadingIcon: "path",
    category: "map",
    color: "green-400",
    defaults: {
      minOptions: 2,
      options: 2,
    },
  } satisfies PageTypeConfigItem,
  area: {
    leadingIcon: "area",
    category: "map",
    color: "green-500",
    defaults: {
      minOptions: 2,
      options: 2,
    },
  } satisfies PageTypeConfigItem,
  section: {
    leadingIcon: "paragraph",
    category: "structure",
    color: "neutral-400",
    structuralPage: true,
  } satisfies PageTypeConfigItem,
  demographics: {
    leadingIcon: "user-fill",
    color: "neutral-400",
    hideInSelection: true,
    structuralPage: true,
  } satisfies PageTypeConfigItem,
} as const;

interface LocalizedPageTypeConfig extends PageTypeConfigItem {
  value: Question["qtype"];
  text: string;
  categoryLabel: string;
}
export const pageTypes = ({
  t,
}: {
  t?: TFunction<"translation", undefined>;
} = {}) => {
  const textMap = {
    text: `${t?.("survey:pageTypes.textResponse")}`,
    number: t?.("survey:pageTypes.number"),
    radio: t?.("survey:pageTypes.multipleChoice"),
    imageChoice: t?.("survey:pageTypes.imageChoice"),
    radioMatrix: t?.("survey:pageTypes.matrix"),
    budget: t?.("survey:pageTypes.participatoryBudget"),
    ranking: t?.("survey:pageTypes.ranking"),
    ratingScale: t?.("survey:pageTypes.scale"),
    slider: t?.("survey:pageTypes.slider"),
    stars: t?.("survey:pageTypes.stars"),
    point: t?.("survey:pageTypes.setPoints"),
    path: t?.("survey:pageTypes.setRoutes"),
    area: t?.("survey:pageTypes.setAreas"),
    section: t?.("survey:pageTypes.section"),
    overview: t?.("survey:pageTypes.overview"),
    intro: t?.("survey:pageTypes.intro"),
    outro: t?.("survey:pageTypes.outro"),
    demographics: t?.("survey:pageTypes.demographicData"),
  };

  const categoryLabels = {
    text: t?.("global:text"),
    selection: t?.("global:selection"),
    rating: t?.("global:rating"),
    priority: t?.("global:priorization"),
    map: t?.("global:map"),
    structure: t?.("global:structure"),
  };

  return Object.entries(pageTypeConfig).map(
    ([key, value]) =>
      ({
        ...value,
        categoryLabel:
          "category" in value ? categoryLabels[value.category] : "",
        value: key,
        text: textMap[key],
      }) as LocalizedPageTypeConfig,
  );
};

export type PageTypeConfig = typeof pageTypeConfig;
export type PageType = keyof PageTypeConfig;
